import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScanLine, AlertTriangle, ShieldCheck } from 'lucide-react';
import { api } from '../services/api';
import { MAX_DOCUMENT_SIZE_MB } from '../constants/upload';
import ScanUpload from '../components/ScanUpload';
import ChatHistory from '../components/ChatHistory';
import './ScansPage.css';

function ScansPage() {
  const { t } = useTranslation(['scans', 'common']);
  const [creating, setCreating] = useState(false);
  const [chats, setChats] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = async () => {
    try {
      const data = await api.getChats();
      setChats(data);
    } catch (error) {
      console.error('Failed to load chats:', error);
    }
  };

  const scanChats = useMemo(() => chats.filter((c) => c.is_scanned), [chats]);

  const handleUploadStart = () => setCreating(true);

  const handleUpload = async (file, options) => {
    try {
      const chat = await api.createChat(file.name);
      await api.uploadScan(file, chat.id, options);
      navigate(`/chat/${chat.id}`);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const code = detail?.error_code;

      // Reverse proxy 413 (request body bigger than client_max_body_size)
      // never reaches the backend, so `detail` is empty / HTML - translate
      // status directly instead of falling through to the generic message.
      if (code === 'PACKAGE_TOO_LARGE') {
        alert(t('errors.packageTooLarge'));
      } else if (status === 413) {
        alert(t('errors.fileTooLarge', { maxMb: MAX_DOCUMENT_SIZE_MB }));
      } else if (code === 'FILE_TOO_LARGE') {
        alert(t('errors.fileTooLarge', { maxMb: detail.params?.max_mb }));
      } else if (code === 'TOO_MANY_PAGES') {
        alert(t('errors.tooManyPages', { maxPages: detail.params?.max_pages }));
      } else if (code === 'OCR_FAILED') {
        alert(t('errors.ocrFailed', { page: detail.params?.page }));
      } else if (code === 'DOCX_HAS_NO_SCANS' || code === 'EMPTY_PDF') {
        alert(t('errors.emptyScan'));
      } else if (code === 'CHAR_LIMIT_REACHED') {
        // The global 403 handler in api.js already opens the top-up modal.
      } else if (code === 'MONTHLY_LIMIT_REACHED') {
        alert(t('errors.monthlyLimit'));
      } else if (detail?.message) {
        alert(detail.message);
      } else {
        alert(t('errors.uploadFailed'));
      }
      setCreating(false);
    }
  };

  return (
    <div className="scans-page">
      <header className="scans-header">
        <div className="scans-title">
          <ScanLine size={28} className="scans-icon" />
          <h1>{t('title')}</h1>
        </div>
        <p className="scans-subtitle">{t('subtitle')}</p>
      </header>

      <div className="scans-upload-section">
        <ScanUpload
          onUpload={handleUpload}
          onUploadStart={handleUploadStart}
          disabled={creating}
        />
        <p className="scan-disclaimer">
          <AlertTriangle size={13} className="scan-disclaimer-icon" />
          <span>{t('common:scanDisclaimer')}</span>
        </p>
        <p className="scan-disclaimer" data-testid="layout-guarantee">
          <ShieldCheck size={13} className="scan-disclaimer-icon" />
          <span>{t('layoutGuarantee')}</span>
        </p>
      </div>

      <h2 className="scans-history-heading">{t('history.heading')}</h2>
      <ChatHistory
        chats={scanChats}
        onDeleted={(id) => setChats((prev) => prev.filter((c) => c.id !== id))}
        onRenamed={(id, name) => setChats((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)))}
        emptyIcon={ScanLine}
        emptyTitle={t('history.empty.noScans')}
        emptyBody={t('history.empty.uploadFirst')}
        cardIcon={ScanLine}
      />
    </div>
  );
}

export default ScansPage;
